import React, { useState, useRef } from 'react';
import { Button, Modal } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import useWebSocket from 'react-use-websocket';
import Config from '../config.json';
import LoginCheck from '../pages/logincheck';
import '../../custom.css';

const WS_URL = 'wss://6aiolymuhf.execute-api.us-east-2.amazonaws.com/prod';

const ArtemisAI = () => {
    const fileInputRef = useRef(null);
    const [artemisData, setArtemisData] = useState('');
    const [dataFilterReportType, setDataFilterReportType] = useState('');
    const [dataFilterDateRange, setDataFilterDateRange] = useState('');
    const [dataFilterPersonName, setDataFilterPersonName] = useState('');
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [presignedUrl, setPresignedUrl] = useState('');
    const [showLogicModal, setShowLogicModal] = useState(false);
    const [showUploadContainer, setShowUploadContainer] = useState(false);
    const [statusUpdate, setStatusUpdate] = useState('');
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [userId] = useState(localStorage.getItem('user_id'));
    const [vettedLogic, setVettedLogic] = useState('');

    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [isOpen, setIsOpen] = useState(false);

    const { getWebSocket } = useWebSocket(WS_URL + '?sourceSystemUrl=' + window.location.href + '&userId=' + userId, {
        onOpen: () => {
            console.log('Connected as ' + userId);
        },
        onMessage: (event) => {
            const message = JSON.parse(event.data).message;

            if (message.includes('https://')) {
                setPresignedUrl(message);
                setStatusUpdate('');
            } else {
                setStatusUpdate(message);
                setPresignedUrl('');
            }
            
            setSubmitDisabled(false);
        },
        shouldReconnect: (closeEvent) => true
    });

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const droppedFile = e.dataTransfer.files[0];
        validateAndSetFile(droppedFile);
    };

    const handleDragEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files[0];
        validateAndSetFile(selectedFile);
    };

    const handleLogicSubmit = async () => {
        const logicInput = document.getElementById('logicInput').value;
        if (logicInput.trim() === '') {
            setStatusUpdate('Logic input cannot be empty.');
            return;
        }

        // try {
        //     const response = await fetch('https://qgukadh2hg.execute-api.us-east-2.amazonaws.com/artemisdetectionrequest', {
        //         method: 'POST',
        //         headers: {
        //             'Authorization': Config.artemisdetectionrequest,
        //             'Content-Type': 'application/json',
        //             'X-Request-ID': requestId()
        //         },
        //         body: JSON.stringify({ logic: logicInput.trim() })
        //     });

        //     if (response.ok) {
        //         setStatusUpdate(file.name + ' uploaded successfully...');
        //         setFile(null);
        //     } else {
        //         throw new Error('File upload failed');
        //     }
        // } catch (error) {
        //     setError(error.message || 'An unexpected error occurred. Please try again.');
        // } finally {
        //     setSubmitDisabled(false);
        // }
        
        console.log('Logic submitted:', logicInput);
        setVettedLogic('Your logic has been successfully vetted and is ready to be used.');
    };

    const handleSelect = (e) => {
        // Only proceed if the target is an input element (date selection)
        if (e.target.tagName.toLowerCase() === 'input' && e.target.type === 'date') {
            const date = new Date(e.target.value);
            let newDateRange;
            if (!dateRange.from) {
                newDateRange = { from: date, to: null };
            } else if (dateRange.from && !dateRange.to) {
                newDateRange = { ...dateRange, to: date };
                setIsOpen(false);
            } else {
                newDateRange = { from: date, to: null };
            }
            setDateRange(newDateRange);

            const dateRangeString = `from ${formatDate(newDateRange.from)} to ${formatDate(newDateRange.to)}`;
            setDataFilterDateRange(dateRangeString);
            console.log(dateRangeString);
        }
    };

    const formatDate = (date) => {
        return date ? date.toISOString().split('T')[0] : '';
    };

    const validateAndSetFile = (attachedFile) => {
        console.log(attachedFile.name);

        const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
        if (attachedFile && allowedTypes.includes(attachedFile.type)) {
            setFile(attachedFile);
            setError('');
            setPresignedUrl('');
            setStatusUpdate('');
        } else {
            setFile(null);
            setError('Please upload only Excel or CSV files.');
        }
    };

    const getContentType = (fileName) => {
        if (fileName.endsWith('.xlsx')) {
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (fileName.endsWith('.xls')) {
            return 'application/vnd.ms-excel';
        } else if (fileName.endsWith('.csv')) {
            return 'text/csv';
        }
        
        // Default to binary data for unknown types.
        return 'application/octet-stream';
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file to upload.');
            return;
        }

        setStatusUpdate(file.name + ' will now be uploaded to the server...');        
        setSubmitDisabled(true);
        console.log('Uploading ' + file.name);

        try {
            const response = await fetch('https://qgukadh2hg.execute-api.us-east-2.amazonaws.com/artemisdetectionrequest', {
                method: 'POST',
                headers: {
                    'Authorization': Config.artemisdetectionrequest,
                    'Content-Type': getContentType(file.name),
                    'X-Original-Filename': file.name,
                    'X-Request-ID': requestId()
                },
                body: file,
            });

            if (response.ok) {
                setStatusUpdate(file.name + ' uploaded successfully...');
                setFile(null);
            } else {
                throw new Error('File upload failed');
            }
        } catch (error) {
            setError('An error occurred while uploading the file. Please try again.');
        } finally {
            setSubmitDisabled(false);
        }
    };

    const hideLogicModal = () => {
        setShowLogicModal(false);
        setVettedLogic('');
    };
    
    const requestId = () => {
        return `${localStorage.getItem('user_id')}-${localStorage.getItem('optimus')}`;
    }

    const LinkRenderer = ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
    
    const MarkdownRenderer = ({ children }) => {
        const customRenderers = {
            a: LinkRenderer,
            break: () => <br />,
            paragraph: ({ node, ...props }) => <p style={{ marginBottom: '1em' }} {...props} />,
            strong: ({ node, ...props }) => <span style={{ fontWeight: 'bold' }} {...props} />
        };
    
        return (
            <ReactMarkdown components={customRenderers}>
                {children}
            </ReactMarkdown>
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        console.log(name, value);
        switch (name) {
            case 'reportType':
                setDataFilterReportType(value);
                break;
            case 'dateRange':
                setDataFilterDateRange(value);
                break;
            case 'personName':
                setDataFilterPersonName(value);
                break;
            default:
                console.warn(`Unhandled input name: ${name}`);
        }
    };

    const handleDataFilterSubmit = async () => {
        try {
            setSubmitDisabled(true);
            setError(null);
            setStatusUpdate('Submitting data filter...');

            const response = await fetch('https://md48j1srf4.execute-api.us-east-2.amazonaws.com/artemisdatarequest', {
                method: 'POST',
                headers: {
                    'Authorization': Config.artemisdatarequest,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    report_type: dataFilterReportType,
                    date_range: dataFilterDateRange,
                    person_name: dataFilterPersonName,
                    request_id: requestId()
                })
            });

            if (response.ok) {
                const result = await response.json();
                setStatusUpdate('Data filter applied successfully.');
                console.log(result);
            } else {
                throw new Error('Data filter application failed');
            }
        } catch (error) {
            setError('An error occurred while applying the data filter. Please try again.');
        } finally {
            setSubmitDisabled(false);
        }
    };

    return (
        <div>
            <LoginCheck />

            <h1 className="page-header gradient-text">
                <span className="gradient-text">Artemis AI</span> <span className='header-tagline'>Uncovers hidden critical patterns in your data</span>
            </h1>

            {/* Input fields for additional options and data entry */}
            <div style={{marginBottom: '20px', padding: '20px', backgroundColor: '#1e1e1e', borderRadius: '8px', boxShadow: '0 2px 4px rgba(255, 255, 255, 0.1)'}}>
                <div className="d-flex flex-wrap align-items-center">
                    {/* Dropdown for AI Report selection */}
                    <select name="reportType" className="form-select me-2 mb-2 mb-md-0" aria-label="Select option" style={{flex: '1', minWidth: '200px', backgroundColor: '#2c2c2c', color: '#ffffff', border: '1px solid #444'}} value={dataFilterReportType} onChange={handleFilterChange}>
                        <option value="" disabled>Choose an AI Report</option>
                        <option value="Red Flags" style={{backgroundColor: '#ffffff', color: '#000000'}}>Red Flags</option>
                        <option value="Sales Offers" style={{backgroundColor: '#ffffff', color: '#000000'}}>Sales Offers</option>
                    </select>
                    
                    {/* Date Range Picker */}
                    <div className="me-2 mb-2 mb-md-0" style={{flex: '1', minWidth: '200px', position: 'relative'}}>
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="form-select"
                            style={{
                                width: '100%',
                                backgroundColor: '#2c2c2c',
                                color: '#ffffff',
                                border: '1px solid #444',
                                textAlign: 'left',
                                padding: '0.375rem 0.75rem',
                                lineHeight: '1.5',
                                appearance: 'none'
                            }}
                            name="dateRange"
                        >
                            {dateRange.from && dateRange.to
                                ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
                                : dateRange.from
                                    ? formatDate(dateRange.from)
                                    : '📅 Pick a date range'}
                        </button>
                        {isOpen && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                right: '0',
                                marginTop: '5px',
                                padding: '10px',
                                backgroundColor: '#1e1e1e',
                                color: '#ffffff',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                zIndex: 10
                            }}>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                    <div>
                                        <label style={{marginBottom: '5px', display: 'block'}}>Start Date:</label>
                                        <input
                                            type="date"
                                            value={formatDate(dateRange.from)}
                                            onChange={handleSelect}
                                            style={{
                                                width: '100%',
                                                backgroundColor: '#2c2c2c',
                                                color: '#ffffff',
                                                border: '1px solid #444',
                                                padding: '5px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{marginBottom: '5px', display: 'block'}}>End Date:</label>
                                        <input
                                            type="date"
                                            value={formatDate(dateRange.to)}
                                            onChange={handleSelect}
                                            min={formatDate(dateRange.from)}
                                            style={{
                                                width: '100%',
                                                backgroundColor: '#2c2c2c',
                                                color: '#ffffff',
                                                border: '1px solid #444',
                                                padding: '5px'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Input field for Person's name */}
                    <input 
                        name="personName"
                        type="text" 
                        className="form-control me-2 mb-2 mb-md-0" 
                        placeholder="Person's name"
                        style={{flex: '1', minWidth: '200px', backgroundColor: '#2c2c2c', color: '#ffffff', border: '1px solid #444'}}
                        value={dataFilterPersonName}
                        onChange={handleFilterChange}
                    />

                    <button 
                        className="btn btn-primary mb-2 mb-md-0" 
                        style={{flex: '0 0 auto', backgroundColor: '#0056b3', border: 'none', color: '#ffffff', minWidth: '100px'}}
                        onClick={handleDataFilterSubmit}
                    >
                        Submit
                    </button>
                </div>
            </div>

            {/* Upload Data button */}
            <div style={{maxWidth: '800px', margin: '40px auto', textAlign: 'center'}}>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowUploadContainer(!showUploadContainer)}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: 'none'
                    }}
                >
                    {showUploadContainer ? 'Hide Upload' : 'Upload Data'}
                </button>
            </div>

            {/* Container for file upload */}
            {showUploadContainer && (
            <div style={{maxWidth: '800px', margin: '40px auto', padding: '24px', backgroundColor: '#aef2f5', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'}}>
                <div
                    style={{
                        border: '2px dashed #1890ff',
                        borderRadius: '8px',
                        padding: '64px',
                        textAlign: 'center',
                        backgroundColor: isDragging ? '#e6f7ff' : 'transparent'
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{margin: '0 auto', color: '#999'}}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <p style={{marginTop: '8px', fontSize: '14px', color: '#666'}}>
                        Drag and drop your Excel or CSV file here, or
                    </p>
                    <button
                        onClick={() => fileInputRef.current.click()}
                        style={{marginTop: '8px', color: '#1890ff', background: 'none', border: 'none', cursor: 'pointer'}}
                    >
                        Choose a File
                    </button>
                    <input
                        key={Math.random()}  // Remount the component.
                        ref={fileInputRef}
                        type="file"
                        style={{display: 'none'}}
                        onChange={handleFileInputChange}
                        accept=".xlsx,.xls,.csv"
                    />
                </div>

                {file && (
                    <div style={{color: '#0d5e18', marginTop: '24px', padding: '8px', backgroundColor: 'none', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontSize: '14px', fontWeight: 'bold',  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{file.name}</span>
                        <button
                            onClick={() => {
                                setFile(null);
                                setSubmitDisabled(false);
                                setError('');
                            }}
                            style={{color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer'}}
                        >
                            ✕
                        </button>
                    </div>
                )}

                {error && (
                    <div style={{marginTop: '24px', padding: '8px', backgroundColor: 'none', borderRadius: '4px', color: '#ff4d4f', display: 'flex', alignItems: 'center'}}>
                        {/* ⚠️ */}
                        <span style={{fontSize: '14px'}}>{error}</span>
                    </div>
                )}

                <button
                    onClick={handleUpload}
                    className="btn btn-primary"
                    disabled={submitDisabled}
                    style={{
                        marginTop: '24px',
                        width: '100%',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: 'none'
                    }}
                >
                    Upload
                </button>
            </div>
            )}

            {/* <div className="mt-3" style={{ textAlign: 'center', color: 'lime', fontSize: '14px', marginTop: '20px', fontWeight: '100'}}>
                <MarkdownRenderer>
                    {statusUpdate}
                </MarkdownRenderer>
            </div> */}

            {presignedUrl ?
                <div style={{
                    maxWidth: '345px',
                    margin: '40px auto',
                    padding: '24px',
                    backgroundColor: '#a2f5b6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px'
                    }}>
                    <div style={{ color: '#006400' }}>
                        <strong>Successful Artemis detection!</strong><br /><br />
                        A link to your Artemis AI Report has been emailed to {userId}.<br /><br />
                        You can also download the report here:<br /><br />
                        <a href={presignedUrl} style={{ color: 'blue' }}>Artemis AI Report</a>
                    </div>
                </div>
                : 
                <div className="mt-3" style={{ textAlign: 'center', color: 'lime', fontSize: '14px', marginTop: '20px', fontWeight: '100'}}>
                    {statusUpdate}
                </div>
            }

            {/* <button
                onClick={() => setShowLogicModal(true)}
                className="btn btn-secondary"
                style={{
                    backgroundColor: '#4CAF50', // A green color
                    color: 'white', // White text for contrast
                    marginTop: '24px',
                    display: 'block',
                    margin: '0 auto',
                    width: '20%',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: 'none'
                }}
            >
                Create New Logic
            </button> */}

            <Modal 
                show={showLogicModal} 
                onHide={hideLogicModal} 
                dialogClassName="custom-modal"
                className="transparent-modal"
                style={{ background: "linear-gradient(to right, #224be0, #feb47b)", width: "400px" }}
                centered>
                <Modal.Header closeButton>
                    <Modal.Title className="gradient-text">
                        {'Create Logic'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ overflowY: "auto" }}>
                    <div className="form-group">
                        <label htmlFor="logicInput">Enter your logic:</label>
                        <textarea
                            id="logicInput"
                            className="form-control"
                            rows="4"
                            style={{
                                width: '100%',
                                padding: '10px',
                                marginBottom: '15px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                resize: 'vertical'
                            }}
                        ></textarea>
                    </div>

                    <Button 
                        variant="primary" 
                        onClick={handleLogicSubmit}
                        style={{ 
                            backgroundColor: "#4CAF50", 
                            border: "none", 
                            width: "100%",
                            padding: '10px',
                            borderRadius: '4px'
                        }}
                    >
                        Submit Logic
                    </Button>

                    <div style={{ marginTop: '15px', fontSize: '16px', color: 'white' }}>
                        {vettedLogic}
                    </div>
                    
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div>
                        <Button variant="secondary" onClick={hideLogicModal} style={{ backgroundColor: "#224be0", width: "120px" }}>Close</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ArtemisAI;